import api from '@api';
import { Feedback as FeedbackOption } from '@enums';
import { useWindowSize } from '@hooks';
import CloseDialogIcon from '@icons/close-dialog.svg?react';
import ThumbsDownIcon from '@icons/thumbs-down.svg?react';
import ThumbsUpIcon from '@icons/thumbs-up.svg?react';
import { useChatStore } from '@store/chat';
import { useShellStore } from '@store/shell';
import { OutlinedButton, PrimaryButton, SecondaryButton } from '@ui/buttons';
import { DialogModal } from '@ui/dialogModal';
import { FormInput } from '@ui/forms';
import { Tooltip } from '@ui/tooltip';
import { log } from '@utils';
import type { ChangeEvent } from 'react';
import { Fragment, useEffect, useRef, useState } from 'react';
import Styled from './Feedback.styled';

function Feedback() {
  const chatId = useChatStore((state) => state.chat.id);
  const getRelatedPromptMessage = useChatStore(
    (store) => store.getRelatedPromptMessage
  );
  const setToast = useShellStore((state) => state.setToast);
  const windowSize = useWindowSize();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const feedbackInputRef = useRef<HTMLInputElement>(null);
  const [feedback, setFeedback] = useState<FeedbackOption>(FeedbackOption.None);
  const [selectedReasons, setSelectedReasons] = useState<string[]>([]);
  const [feedbackText, setFeedbackText] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  useEffect(() => {
    if (selectedReasons.length > 0) {
      log('Selected reasons:', { data: [...selectedReasons] });
    }
  }, [selectedReasons]);

  useEffect(() => {
    if (feedback === FeedbackOption.ThumbsDown) {
      wrapperRef.current
        ?.querySelector('.feedback-reasons')
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [feedback]);

  useEffect(() => {
    if (isDialogOpen && windowSize.isLarge) {
      window.requestAnimationFrame(() => feedbackInputRef.current?.focus());
    }
  }, [isDialogOpen, windowSize.isLarge]);

  const handleFeedback = (nextFeedback: FeedbackOption) => {
    // If the feedback is the same, do nothing
    if (feedback === nextFeedback) {
      setFeedback(FeedbackOption.None);
      return;
    }

    setFeedback(nextFeedback);

    if (nextFeedback === FeedbackOption.ThumbsUp) {
      setSelectedReasons([]);
      setFeedbackText('');
      // Track positive feedback reported
      api.analytics.reportedPositiveFeedback({
        ['chat_id']: chatId,
        ['query']: getRelatedPromptMessage().prompt
      });
    } else {
      // Track negative feedback selected
      api.analytics.selectedNegativeFeedback({
        ['chat_id']: chatId,
        ['query']: getRelatedPromptMessage().prompt
      });
    }
  };

  const handleSelectReason = (reason: string) => {
    if (reason === 'More...') {
      setIsDialogOpen(true);
      api.analytics.viewedMoreNegativeFeedbackReasons({
        ['chat_id']: chatId,
        ['query']: getRelatedPromptMessage().prompt
      });
      return;
    }

    const negativeFeedbackProps = {
      ['chat_id']: chatId,
      ['origin']: isDialogOpen ? 'popup' : 'chat',
      ['query']: getRelatedPromptMessage().prompt,
      ['reason_category']: reason
    } as const;

    if (selectedReasons.includes(reason)) {
      // Remove the reason from the set
      setSelectedReasons((selectedReasons) =>
        [...selectedReasons].filter(
          (selectedReason) => selectedReason !== reason
        )
      );

      api.analytics.undidNegativeFeedback(negativeFeedbackProps);
    } else {
      const newSelectedReasons = [...selectedReasons, reason];

      // Add the reason to the set
      setSelectedReasons(newSelectedReasons);

      // Track only if the more feedback dialog is not open
      api.analytics.reportedNegativeFeedback(negativeFeedbackProps);
    }
  };

  const handleFeedbackTextChange = (
    event: ChangeEvent<HTMLTextAreaElement>
  ) => {
    setFeedbackText(event.target.value.trim());
  };

  const handleSubmit = () => {
    if (feedbackText || selectedReasons.length > 0) {
      setIsDialogOpen(false);
    }

    if (feedbackText) {
      // Track negative feedback that is related to the feedback dialog
      api.analytics.reportedNegativeFeedbackDetails({
        ['additional_feedback']: feedbackText,
        ['chat_id']: chatId,
        ['query']: getRelatedPromptMessage().prompt
      });
    }

    setToast({
      message: 'Thanks! Your feedback has been recorded',
      isOpen: true
    });
  };

  const handleCloseMoreFeedbackDialog = () => {
    setIsDialogOpen(false);
  };

  const renderFeedbackReasons = (reasons: string[], uid: string) => (
    <Fragment>
      <Styled.Label data-testid="feedback-reasons-label">
        Tell us more:
      </Styled.Label>
      <Styled.Reasons className="feedback-reasons">
        {reasons.map((reason) => (
          <OutlinedButton
            testId="feedback-reason"
            className={selectedReasons.includes(reason) ? 'selected' : ''}
            key={`${uid}:${reason}`}
            onClick={() => handleSelectReason(reason)}>
            {reason}
          </OutlinedButton>
        ))}
      </Styled.Reasons>
    </Fragment>
  );

  const dialogContent = (
    <Styled.MoreFeedbackWrapper visible>
      {renderFeedbackReasons(
        augmentedFeedbackReasons,
        'augmented_feedback_reasons'
      )}
      <Styled.AdditionalFeedbackWrapper>
        <Styled.SmallLabel id="more-feedback-input-label">
          Feel free to add specific details
        </Styled.SmallLabel>
        <FormInput
          data-testid="more-feedback-input"
          ref={feedbackInputRef}
          inputProps={{
            className: 'feedback-input',
            htmlFor: 'more-feedback-input-label'
          }}
          fullWidth
          multiline={!windowSize.isLarge}
          placeholder={
            windowSize.isLarge
              ? 'Enter additional feedback about the response'
              : 'Enter feedback'
          }
          rows={windowSize.isLarge ? undefined : 2}
          value={feedbackText}
          onChange={handleFeedbackTextChange}
        />
      </Styled.AdditionalFeedbackWrapper>
    </Styled.MoreFeedbackWrapper>
  );

  const dialogFooter = (
    <Styled.DialogFooter>
      <SecondaryButton
        testId="more-feedback-cancel-button"
        onClick={handleCloseMoreFeedbackDialog}>
        Cancel
      </SecondaryButton>
      <PrimaryButton
        testId="more-feedback-submit-button"
        disabled={selectedReasons.length === 0 && feedbackText === ''}
        onClick={handleSubmit}>
        Submit
      </PrimaryButton>
    </Styled.DialogFooter>
  );

  const moreFeedback = (
    <Styled.MoreFeedbackWrapper
      ref={wrapperRef}
      visible={feedback === FeedbackOption.ThumbsDown}>
      {renderFeedbackReasons(
        [...feedbackReasons, 'More...'],
        'feedback_reasons'
      )}
      <DialogModal
        // TODO: Remove this once the dialog is updated with 'className'
        rootclasses={{
          containerRoot: 'feedback-dialog-container'
        }}
        headerButton={
          <Styled.CloseButton data-testid="more-feedback-close-button">
            <CloseDialogIcon onClick={handleCloseMoreFeedbackDialog} />
          </Styled.CloseButton>
        }
        open={isDialogOpen}
        title="Provide more feedback"
        main={dialogContent}
        testId="more-feedback-dialog"
        footerRight={dialogFooter}
      />
    </Styled.MoreFeedbackWrapper>
  );

  return (
    <Fragment>
      <Styled.FeedbackWrapper>
        {feedback === FeedbackOption.None ? (
          <Fragment>
            <Tooltip
              content="Good response"
              placement="bottom-start"
              enterDelay={200}
              enterTouchDelay={200}
              leaveDelay={0}>
              <Styled.Button
                data-testid="feedback-thumbs-up"
                onClick={() => handleFeedback(FeedbackOption.ThumbsUp)}>
                <ThumbsUpIcon />
              </Styled.Button>
            </Tooltip>
            <Tooltip
              content="Bad response"
              placement="bottom-start"
              enterDelay={200}
              enterTouchDelay={200}
              leaveDelay={0}>
              <Styled.Button
                data-testid="feedback-thumbs-down"
                onClick={() => handleFeedback(FeedbackOption.ThumbsDown)}>
                <ThumbsDownIcon />
              </Styled.Button>
            </Tooltip>
          </Fragment>
        ) : (
          <Fragment>
            <Styled.Button
              data-testid={`feedback-thumbs-up${
                feedback === FeedbackOption.ThumbsUp ? '-selected' : ''
              }`}
              selected={feedback === FeedbackOption.ThumbsUp}
              onClick={() => handleFeedback(FeedbackOption.ThumbsUp)}>
              <ThumbsUpIcon />
            </Styled.Button>
            <Styled.Button
              data-testid={`feedback-thumbs-down${
                feedback === FeedbackOption.ThumbsDown ? '-selected' : ''
              }`}
              selected={feedback === FeedbackOption.ThumbsDown}
              onClick={() => handleFeedback(FeedbackOption.ThumbsDown)}>
              <ThumbsDownIcon />
            </Styled.Button>
          </Fragment>
        )}
      </Styled.FeedbackWrapper>
      {moreFeedback}
    </Fragment>
  );
}

const feedbackReasons = [
  "Didn't understand my question",
  'Issues with tone or style',
  'Not enough detail',
  'Factually incorrect',
  'Not relevant',
  'Poor formatting'
];

const augmentedFeedbackReasons = [
  ...feedbackReasons,
  'Biased or misleading',
  'Contradictory or confusing',
  'Too repetitive'
];

export default Feedback;
